import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { ConfirmButton } from "../../utils/ConfirmButton";
import { SearchableMultiSelectReorderModal } from "./SearchableMultiSelectReorderModal";

export interface SearchableMultiSelectProps {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: Record<string, string>;
  readOnly?: boolean;
  hasError?: boolean;
  hint?: string;
}

export default function SearchableMultiSelect({
  id,
  value,
  onChange,
  options,
  readOnly = false,
  hasError = false,
  hint,
}: SearchableMultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const entries = useMemo(
    () =>
      Object.entries(options).map(([optId, label]) => ({
        optId,
        label,
      })),
    [options]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = entries.filter(({ optId }) => !value.includes(optId));
    const list = !q
      ? available
      : available.filter(
          ({ optId, label }) =>
            optId.toLowerCase().includes(q) || label.toLowerCase().includes(q)
        );
    return [...list].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        sensitivity: "base",
      })
    );
  }, [entries, query, value]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  const toggle = (optId: string) => {
    if (readOnly) return;
    if (value.includes(optId)) {
      onChange(value.filter((x) => x !== optId));
    } else {
      onChange([...value, optId]);
    }
  };

  const remove = (optId: string) => {
    if (readOnly) return;
    onChange(value.filter((x) => x !== optId));
  };

  const clearAll = useCallback(() => {
    onChange([]);
    close();
  }, [onChange, close]);

  const handleBeforeReorderModalOpen = useCallback(() => {
    close();
    searchInputRef.current?.blur();
  }, [close]);

  const borderError = hasError
    ? "border-red-500 dark:border-red-400"
    : "border-gray-300 dark:border-gray-700";

  const focusRing = hasError
    ? "focus-within:border-red-500 focus-within:outline-hidden focus-within:ring-1 focus-within:ring-red-500/20 dark:focus-within:border-red-400"
    : "focus-within:border-blue-600 focus-within:outline-hidden focus-within:ring-1 focus-within:ring-brand-500/10 dark:focus-within:border-brand-800";

  return (
    <div ref={containerRef} className="relative">
      {!readOnly && value.length > 0 && (
        <div className="absolute top-[-35px] right-0 flex shrink-0 items-center gap-1">
          {value.length >= 2 && (
            <SearchableMultiSelectReorderModal
              value={value}
              options={options}
              onReorder={onChange}
              onBeforeOpen={handleBeforeReorderModalOpen}
            />
          )}
          <ConfirmButton onConfirm={clearAll} />
        </div>
      )}
      {readOnly && (
        <input
          id={id}
          type="text"
          className="sr-only"
          readOnly
          tabIndex={-1}
          value=""
        />
      )}
      <div
        className={`flex min-h-11 w-full flex-col gap-2 rounded-lg border bg-transparent px-3 py-2.5 text-sm text-gray-800 shadow-theme-xs transition-colors dark:bg-gray-900 dark:text-white/90 ${borderError} ${
          !readOnly ? focusRing : ""
        } ${readOnly ? "cursor-not-allowed opacity-90" : "cursor-text"}`}
        onMouseDown={(e) => {
          if (readOnly) return;
          if ((e.target as HTMLElement).closest("button")) return;
          if ((e.target as HTMLElement).closest('input[type="search"]')) return;
          setOpen(true);
          queueMicrotask(() => searchInputRef.current?.focus());
        }}
      >
        {value.length > 0 &&
          (readOnly ? (
            <div className="flex flex-wrap gap-1.5">
              {value.map((optId) => (
                <span
                  key={optId}
                  className="inline-flex max-w-full items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-900 select-none dark:bg-gray-800 dark:text-blue-100"
                >
                  <span className="truncate">{options[optId] ?? optId}</span>
                </span>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {value.map((optId) => (
                <SelectedTagChip
                  key={optId}
                  label={options[optId] ?? optId}
                  onRemove={() => remove(optId)}
                  removeLabel={t("form.removeTag")}
                />
              ))}
            </div>
          ))}

        {!readOnly && (
          <div className="flex items-center gap-2">
            <Search
              className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
              aria-hidden
            />
            <input
              ref={searchInputRef}
              id={!readOnly ? id : undefined}
              type="search"
              autoComplete="off"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-white/90 dark:placeholder:text-white/30"
              placeholder={t("search")}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
          </div>
        )}
      </div>

      {open && !readOnly && (
        <ul
          className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-900"
          role="listbox"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400">
              {t("form.noResults")}
            </li>
          ) : (
            filtered.map(({ optId, label }) => (
              <li key={optId} role="option">
                <button
                  type="button"
                  className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-white/90 dark:hover:bg-gray-800"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggle(optId)}
                >
                  <span className="min-w-0 truncate">{label}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      {hint ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
}

interface SelectedTagChipProps {
  label: string;
  onRemove: () => void;
  removeLabel: string;
}

function SelectedTagChip({
  label,
  onRemove,
  removeLabel,
}: SelectedTagChipProps) {
  return (
    <span className="inline-flex max-w-full items-center gap-0.5 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-900 select-none dark:bg-gray-800 dark:text-blue-100">
      <span className="min-w-0 truncate px-0.5">{label}</span>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 hover:bg-blue-200/80 dark:hover:bg-gray-700"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={removeLabel}
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </span>
  );
}
